import update from 'immutability-helper';
import lodash from 'lodash';

import { updatePreset } from '@src/api/emp/query';
import { EXPLORE_PRESET } from '@src/models/presets';
import { getDimensionListFromPreset } from '@src/utils/getDimensionListFromPreset';
import { getColor, getPresetFid, isGrandfatherFilter } from '@utils';
import { LAYOUT_MAP, MODAL_NONE } from '@utils/constants';
import featureNames from '@utils/featureToggle/constants';

import * as types from './types';

const initialFilter = {
  name: 'Filters',
  rule: [],
};

export const initialState = {
  appliedFilters: [lodash.cloneDeep(initialFilter)],
  isGrandfatherFilter: false,
  filterColor: '',
  requestsSent: 0,
  responsesReceived: 0,
  showMask: false,
  dashboard: {
    list: [
      {
        id: 'trends',
        name: 'Trends',
        context: '',
      },
    ],
    activeId: 'trends',
  },
  interval: { from: '', to: '', shortcut: '', granularity: '' },
  modal: {
    modalType: MODAL_NONE,
  },
  /* -- initialze - InitData START */
  profiles: [],
  features: {
    // Following features have pre-determined defaults. Features API response determines
    // the customer specific behavior of the feature
    [featureNames.EMP]: true,
    [featureNames.CREATE_FILTER]: true,
    [featureNames.DATA_ACCESS_DURATION]: 24, // months
    [featureNames.APP]: true,
  },
  layout: LAYOUT_MAP.vertical,
  preset: {
    activePreset: '-1',
    presetDimensions: {},
  },
  mappingMode: false,
  initialConfiguration: {
    // customer_enabled:false,
    // username,
    // is_selected,
    // profile_id,
    // primary,
    // tz_name,
    // timezone,
    // customer_name,
    // customer_id,
    // role,
    // hidden,
    // api_key,
  },
};

initialState.filterColor = getColor(0, initialFilter.name);

const reducerMap = Object.create(null);

reducerMap[types.UPDATE_FEATURES] = (state, action) => {
  const { features } = action;
  return update(state, {
    features: {
      $merge: {
        ...features,
      },
    },
  });
};

reducerMap[types.RESET_FILTER] = (state) => {
  return update(state, {
    appliedFilters: {
      $set: [
        {
          name: 'Filters',
          rule: [],
          is_content: false,
        },
      ],
    },
  });
};

reducerMap[types.APPLY_FILTER] = (state, action) => {
  const { filter } = action;
  const { name, rule = [], fid, is_advanced: filterIsAdvanced, isAdvanced } = filter;
  const colorIndex = (rule.flat().length || 1) - 1;
  const filterColor = getColor(colorIndex, name);
  const { features } = state;
  const grandfatherFilter = isGrandfatherFilter(filter, features[featureNames.CUSTOM_TAGS]);

  const extraParams = {};
  // refresh key for reloading trends
  if ('_rKey' in filter) {
    extraParams['_rKey'] = filter['_rKey'];
  }

  // use passed in preset or check for a preset filter match
  const presetFid = filter.presetFid || getPresetFid(rule);

  return update(state, {
    $merge: {
      filterColor,
      appliedFilters: [
        {
          name,
          rule,
          fid,
          isAdvanced: filterIsAdvanced || isAdvanced,
          presetFid,
          ...extraParams,
        },
      ],
      isGrandfatherFilter: grandfatherFilter,
    },
  });
};

reducerMap[types.UPDATE_INITIAL_CONFIGURATION] = (state, action) => {
  const { initialConfiguration } = action;
  return update(state, {
    initialConfiguration: {
      $merge: {
        ...initialConfiguration,
      },
    },
  });
};

reducerMap[types.ASYNC_HTTP_REQ_STARTED] = (state) => {
  const count = state.requestsSent + 1;
  return update(state, {
    $merge: {
      requestsSent: count,
      showMask: true,
    },
  });
};

reducerMap[types.ASYNC_HTTP_REQ_ENDED] = (state) => {
  const count = state.responsesReceived + 1;
  const showMask = state.requestsSent - count !== 0;
  if (showMask) {
    return update(state, {
      $merge: {
        responsesReceived: count,
        showMask,
      },
    });
  }
  return update(state, {
    $merge: {
      requestsSent: 0,
      responsesReceived: 0,
      showMask,
    },
  });
};

reducerMap[types.TOGGLE_MODAL] = (state, action) => {
  const { modalType } = action;
  const modalConfig = {
    modalType,
  };
  return update(state, {
    modal: {
      $set: modalConfig,
    },
  });
};

reducerMap[types.INTERVAL_SELECTION] = (state, action) =>
  update(state, {
    interval: {
      $set: {
        from: action.from,
        to: action.to,
        shortcut: action.shortcut,
      },
    },
  });

reducerMap[types.SET_PRESET] = (state, action) => {
  const {
    preset: { activePreset },
  } = state;
  if (activePreset === EXPLORE_PRESET) {
    const newPreset = action.preset;
    const id = newPreset.presetDimensions[EXPLORE_PRESET].id;
    const array = getDimensionListFromPreset(newPreset);
    updatePreset(id, {
      preset_name: EXPLORE_PRESET,
      dimensions: array,
    });
  }

  return update(state, {
    preset: {
      $set: action.preset,
    },
  });
};

reducerMap[types.CHANGE_ACTIVE_PRESET] = (state, action) => {
  const { activePreset } = action;
  const {
    preset: { presetDimensions },
  } = state;
  if (activePreset !== EXPLORE_PRESET) {
    presetDimensions[activePreset].currentDimensions = lodash.cloneDeep(
      presetDimensions[activePreset].defaultDimensions,
    );
  }

  return update(state, {
    preset: {
      $merge: {
        activePreset,
        presetDimensions,
      },
    },
  });
};

reducerMap[types.SET_MAPPING_MODE_PRESET] = (state) => {
  const {
    preset: { mappingModePreset },
  } = state;
  return update(state, {
    preset: {
      $merge: {
        activePreset: mappingModePreset,
      },
    },
  });
};

reducerMap[types.CHANGE_MAPPING_MODE] = (state, action) =>
  update(state, {
    mappingMode: {
      $set: action.mappingMode,
    },
  });

/* eslint max-len:0 */
const common = (state = initialState, action) =>
  reducerMap[action.type] ? reducerMap[action.type](state, action) : state;

export default common;
